/**
 * Onboarding storyboard store (demo AND real first-run users).
 *
 * Runtime state (``active`` / ``stepIndex`` / ``currentKey``) is ephemeral.
 * Only ``seenBy`` is persisted (via the shared ``zustandStorage``), keyed by
 * audience (``"demo"`` per browser, ``"user:<id>"`` per real user) so each
 * audience is onboarded exactly once and is replayable on demand.
 *
 * SOTA-2026: headless on the design system's own Radix + Framer primitives (see
 * ``DemoTour.tsx``) — no AGPL/bundle/ARIA-conflict risk, fully on-brand.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { zustandStorage } from "@/lib/storage";

interface OnboardingTourState {
  active: boolean;
  stepIndex: number;
  /** Audience key the running tour belongs to (set on ``start``). */
  currentKey: string | null;
  /** Per-audience "already finished/skipped" flags. */
  seenBy: Record<string, boolean>;
  start: (key: string) => void;
  setStepIndex: (index: number) => void;
  finish: () => void;
}

export const useDemoTourStore = create<OnboardingTourState>()(
  persist(
    (set) => ({
      active: false,
      stepIndex: 0,
      currentKey: null,
      seenBy: {},
      start: (key) => set({ active: true, stepIndex: 0, currentKey: key }),
      setStepIndex: (index) => set({ stepIndex: index }),
      finish: () =>
        set((s) => ({
          active: false,
          stepIndex: 0,
          currentKey: null,
          seenBy: s.currentKey ? { ...s.seenBy, [s.currentKey]: true } : s.seenBy,
        })),
    }),
    {
      name: "ando.demo-tour",
      storage: createJSONStorage(() => zustandStorage),
      partialize: (state) => ({ seenBy: state.seenBy }),
    },
  ),
);
